/**
 * ICONIC: Hilfen für die Seite «Kanäle & Leute» (Live-Ansicht und Kanäle verwalten).
 */

/** Trennlinien («[spacer]…», «[cspacer]Text») erkennen */
export function parseSpacer(name) {
  const m = /^\[(\*?)(c|l|r)?spacer[^\]]*\](.*)$/i.exec(String(name || ""));
  if (!m) return null;
  const text = (m[3] || "").trim();
  const repeat = m[1] === "*" || /^(.)\1{2,}$/.test(text);
  return {
    align: (m[2] || "l").toLowerCase(),
    text: repeat ? "" : text,
    line: repeat || !text,
  };
}

/**
 * Baut die Zeilen der Baumansicht: Kanäle in Server-Reihenfolge, darunter die Leute.
 * channellist liefert die Kanäle bereits in der Reihenfolge, die TeamSpeak anzeigt.
 */
export function buildRows(channels, clients, { showEmpty = true, search = "" } = {}) {
  const byParent = {};
  channels.forEach((c) => {
    const pid = String(c.pid);
    (byParent[pid] = byParent[pid] || []).push(c);
  });
  const peopleIn = {};
  clients.forEach((p) => {
    const cid = String(p.cid);
    (peopleIn[cid] = peopleIn[cid] || []).push(p);
  });
  Object.values(peopleIn).forEach((list) =>
    list.sort((a, b) => String(a.clientNickname).localeCompare(String(b.clientNickname)))
  );
  const q = String(search || "").toLowerCase().trim();
  const matches = (p) => !q || String(p.clientNickname).toLowerCase().includes(q);

  // Anzahl Leute im Kanal samt Unterkanälen (für «leere ausblenden»)
  const familyCount = {};
  const count = (cid) => {
    if (familyCount[cid] !== undefined) return familyCount[cid];
    let n = (peopleIn[cid] || []).filter(matches).length;
    (byParent[cid] || []).forEach((c) => (n += count(String(c.cid))));
    familyCount[cid] = n;
    return n;
  };

  const rows = [];
  const walk = (pid, depth) => {
    (byParent[pid] || []).forEach((c) => {
      const cid = String(c.cid);
      const spacer = parseSpacer(c.channelName);
      const people = (peopleIn[cid] || []).filter(matches);
      if ((!showEmpty || q) && !count(cid) && !spacer) return;
      if (spacer && (q || !showEmpty)) return;
      rows.push({ kind: "channel", key: `c${cid}`, depth, channel: c, spacer, people: people.length });
      people.forEach((p) =>
        rows.push({ kind: "person", key: `p${p.clid}`, depth: depth + 1, person: p })
      );
      walk(cid, depth + 1);
    });
  };
  walk("0", 0);
  return rows;
}

/** Geschwister eines Kanals in Anzeige-Reihenfolge */
export function siblingsOf(channels, channel) {
  return channels.filter((c) => String(c.pid) === String(channel.pid));
}

/** Parameter für channelmove, um einen Kanal eine Stelle nach oben (-1) oder unten (+1) zu schieben */
export function moveParams(channels, channel, dir) {
  const sibs = siblingsOf(channels, channel);
  const i = sibs.findIndex((c) => String(c.cid) === String(channel.cid));
  const j = i + dir;
  if (i < 0 || j < 0 || j >= sibs.length) return null;
  // «order» = Kanal, unter dem er danach steht (0 = ganz oben)
  let order;
  if (dir < 0) order = j === 0 ? 0 : sibs[j - 1].cid;
  else order = sibs[j].cid;
  return { cid: channel.cid, cpid: channel.pid, order };
}

export const BAN_DURATIONS = [
  { text: "10 Minuten", value: 600 },
  { text: "1 Stunde", value: 3600 },
  { text: "1 Tag", value: 86400 },
  { text: "1 Woche", value: 604800 },
  { text: "30 Tage", value: 2592000 },
  { text: "Für immer", value: 0 },
];

export function since(seconds) {
  const n = Number(seconds);
  if (!n) return "";
  const d = new Date(n * 1000);
  const today = new Date();
  const hm = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === today.toDateString()) return `seit ${hm} Uhr`;
  return `seit ${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} ${hm} Uhr`;
}
